import { Icon } from '@iconify/react'
import { Button, Slider } from 'antd'
import classNames from 'classnames'
import { useAppSelector } from 'common/hooks/redux'
import { useSceneActions } from 'features/Procedure/hooks/useSceneActions'
import { DrawMode } from 'features/Procedure/state/procedure.type'
import React, { useCallback, useMemo } from 'react'
import {
  lineMarkers,
  lineSizes,
  pinMarkers,
  pinSizes,
  predefinedColors,
} from './customizer.constants'
import styles from './draw-customizer.module.scss'
type Props = {
  drawColor: string
  drawWidth: number
  hide?: boolean
  onColorChange: (color: string) => void
  onWidthChange: (width: number) => void
}

export const DrawCustomizer = React.memo(
  ({ drawColor, drawWidth, onColorChange, onWidthChange, hide = false }: Props) => {
    const drawMode = useAppSelector((state) => state.procedureReducer.drawMode)
    const isOpen = useAppSelector((state) => state.procedureReducer.isDrawCustomizerOpen)
    const { handleSetDrawCustomizerOpen } = useSceneActions()
    const isPin = drawMode === DrawMode.PIN
    const isX = drawMode === DrawMode.X
    const currentSizes = isPin || isX ? pinSizes : lineSizes
    const currentMarkers = isPin || isX ? pinMarkers : lineMarkers

    const toggleIsOpen = useCallback(() => {
      handleSetDrawCustomizerOpen(!isOpen)
    }, [handleSetDrawCustomizerOpen, isOpen])

    const widthValue = useMemo(() => {
      let closestIndex = 0
      let minDiff = Math.abs(drawWidth - currentSizes[0])

      Object.entries(currentSizes).forEach(([index, size]) => {
        const diff = Math.abs(drawWidth - size)
        if (diff < minDiff) {
          minDiff = diff
          closestIndex = Number(index)
        }
      })

      return closestIndex
    }, [drawWidth, currentSizes])

    const handleWidthChangeInternal = useCallback(
      (value: number) => {
        const newSize = currentSizes[value]
        if (newSize !== undefined) {
          onWidthChange(newSize)
        }
      },
      [currentSizes, onWidthChange],
    )

    const handleColorSelect = useCallback(
      (color: string) => {
        onColorChange(color)
      },
      [onColorChange],
    )

    if (hide || !drawMode || drawMode === DrawMode.ERASER) return null

    return (
      <>
        <Button
          type='primary'
          icon={<Icon icon='mdi:chevron-left' fontSize={24} />}
          onClick={toggleIsOpen}
          className={styles.expandButton}
          title='Expand drawing options'
        />
        <div className={classNames(styles.container, { [styles.open]: isOpen })}>
          <div className={styles.header}>
            <h3 className={styles.title}>Drawing Options</h3>
            <Icon
              icon='mdi:close'
              fontSize={20}
              onClick={toggleIsOpen}
              className={styles.closeButton}
            />
          </div>
          <div className={styles.content}>
            <div className={styles.section}>
              <span className={styles.label}>Size</span>
              <Slider
                marks={currentMarkers}
                step={null}
                value={widthValue}
                onChange={handleWidthChangeInternal}
                min={0}
                max={Object.keys(currentMarkers).length - 1}
                tooltip={{ formatter: null }}
              />
            </div>

            <div className={styles.section}>
              <span className={styles.label}>Color</span>
              <div className={styles.colorGrid}>
                {predefinedColors.map(({ label, value }) => (
                  <button
                    key={label}
                    className={classNames(styles.colorButton, {
                      [styles.selected]: drawColor === value,
                    })}
                    style={{ backgroundColor: value }}
                    onClick={() => handleColorSelect(value)}
                    title={label}
                    type='button'
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </>
    )
  },
)
